import { Box, Card, CardProps } from 'theme-ui';
import { AvailableShift, ShiftType } from '@velove/types';

import { formatDate, formatTime } from '../../helpers/date';
import { getShiftTypeLabel } from '../../helpers/labels';

import CardRow from './CardRow';
import CardLabel from './CardLabel';
import CardValue from './CardValue';

// easy state-machine
enum Statuses {
  AVAILABLE = 'available',
  CANCELED = 'canceled',
  NONE = 'none', // used to display only the detail of the availableShift, without actions
  UPCOMING = 'upcoming'
}

export type Status = keyof typeof Statuses;

export interface ShiftCardProps extends CardProps {
  actions?: React.ReactNode;
  header?: React.ReactNode;
  rows?: React.ReactNode;
  status: Status;
  availableShift: AvailableShift;
  labels: {
    type: string;
    partner: string;
    hub: string;
    date: string;
    time: string;
  };
}

const ShiftCard: React.FC<ShiftCardProps> = ({
  actions,
  availableShift,
  header,
  labels,
  rows,
  status,
  sx,
  ...props
}) => {
  let startAt: Date | undefined;
  let endAt: Date | undefined;
  let borderColor: string | undefined;

  try {
    startAt = new Date(availableShift.startAt);
    endAt = new Date(availableShift.endAt);
  } catch (error) {} // eslint-disable-line no-empty

  switch (status) {
    case 'AVAILABLE':
      borderColor = 'systemBlue';
      break;
    case 'UPCOMING':
      borderColor = 'systemGreen';
      break;
    case 'CANCELED':
      borderColor = 'veloDarkBlue';
      break;
  }

  if (!['CANCELED'].includes(status) && endAt && endAt.getTime() < Date.now()) {
    borderColor = 'veloGrey2';
  }

  return (
    <Card
      variant="white"
      sx={{
        mb: 'm',
        px: 'm',
        py: 'xs',
        borderLeft: 'solid',
        borderLeftWidth: borderColor ? 'fat' : 'borderless',
        borderColor,
        boxShadow: 'medium',
        ...sx
      }}
      {...props}
      ref={null}
    >
      {header && (
        <Box
          as="header"
          sx={{
            p: 'm',
            mx: '-m',
            mt: '-xs',
            borderBottom: 'solid',
            borderBottomWidth: 'regular',
            borderBottomColor: 'veloGrey3'
          }}
        >
          {header}
        </Box>
      )}
      <Box>
        {[ShiftType.DELIVERY].includes(availableShift.type as ShiftType) && availableShift.partner ? (
          <CardRow withBottomBorder>
            <CardLabel>{labels.partner}</CardLabel>
            <CardValue>{availableShift.partner.name}</CardValue>
          </CardRow>
        ) : (
          <CardRow withBottomBorder>
            <CardLabel>{labels.type}</CardLabel>
            <CardValue sx={{ textTransform: 'capitalize' }}>{getShiftTypeLabel(availableShift.type)}</CardValue>
          </CardRow>
        )}
        {startAt && (
          <CardRow withBottomBorder>
            <CardLabel>{labels.date}</CardLabel>
            <CardValue>{formatDate(startAt)}</CardValue>
          </CardRow>
        )}
        {startAt && endAt && (
          <CardRow withBottomBorder>
            <CardLabel>{labels.time}</CardLabel>
            <CardValue>{`${formatTime(startAt)}-${formatTime(endAt)}`}</CardValue>
          </CardRow>
        )}
        <CardRow>
          <CardLabel>{labels.hub}</CardLabel>
          <CardValue>{availableShift.hub.name}</CardValue>
        </CardRow>
        {rows}
      </Box>
      {actions}
    </Card>
  );
};

export default ShiftCard;
